import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Collapse } from "bootstrap";

function Sidebar() {
  const [isMini, setIsMini] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [activeItem, setActiveItem] = useState("");
  const [userTypes, setUserTypes] = useState("");
  const sidebarRef = useRef(null);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);

  // Sync active item with current route
  useEffect(() => {
    const pathToItemMap = {
      "/": "home",

      "/trending": "trending",
      "/podcasts": "podcasts",
      // Dynamic podcast routes (match by prefix)
      "/podcast-episode": "podcasts",
      // Backward/typo-safe route prefix used in some environments
      // "/episode": "podcasts",
      // "/": "store",
      "/subscription": "subscription",
      "/events": "events",
      "/news": "news",
      "/playlist": "playlist",
      "/playlists": "playlist",
      "/like": "playlist",
      "/artist/dashboard": "dashboard",
      "/artist/album": "album",
      "/artist/song": "song",
      "/artist/podcasts": "podcasts",
      "/artist/events": "events",
    };

    const pathname = location.pathname;

    // Match by pathname: "/" only for exact match, others by prefix (longest first)
    const matchedKey = Object.keys(pathToItemMap)
      .sort((a, b) => b.length - a.length)
      .find((key) =>
        key === "/" ? pathname === "/" : pathname.startsWith(key),
      );

    const currentItem = pathToItemMap[matchedKey] || "home";
    setActiveItem(currentItem);
    localStorage.setItem("activeNavItem", currentItem);
  }, [location.pathname]);

  // Initialize sidebar
  useEffect(() => {
    const savedTypes = localStorage.getItem("current_user") || "";
    setUserTypes(savedTypes);

    const handleResize = () => {
      sidebarInit();
    };

    sidebarInit();
    initializeSidebar();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      cleanupSidebarEvents();
    };
  }, []);

  const sidebarInit = useCallback(() => {
    const isMobileView = window.innerWidth < 1200;
    setIsMobile(isMobileView);
    // On mobile, sidebar should be closed by default (overlay mode)
    // On desktop, use mini state
    if (isMobileView) {
      setIsMobileOpen(false); // Closed by default on mobile
      setIsMini(false); // Don't use mini mode on mobile
    } else {
      setIsMini(false); // Desktop: start with full sidebar
      setIsMobileOpen(false); // Not applicable on desktop
    }
  }, []);

  const initializeSidebar = () => {
    if (!sidebarRef.current) return;

    const activeItems = sidebarRef.current.querySelectorAll(".active");
    activeItems.forEach((elem) => {
      if (!elem.closest("ul").classList.contains("iq-main-menu")) {
        const childMenu = elem.closest("ul");
        const parentMenu = childMenu.closest("li").querySelector(".nav-link");
        parentMenu.classList.add("active");

        new Collapse(childMenu, {
          toggle: true,
        });
      }
    });

    const collapseElements = sidebarRef.current.querySelectorAll(".collapse");
    collapseElements.forEach((collapseEl) => {
      collapseEl.addEventListener("show.bs.collapse", handleCollapseShow);
      collapseEl.addEventListener("hidden.bs.collapse", handleCollapseHide);
    });
  };

  const cleanupSidebarEvents = () => {
    if (!sidebarRef.current) return;

    const collapseElements = sidebarRef.current.querySelectorAll(".collapse");
    collapseElements.forEach((collapseEl) => {
      collapseEl.removeEventListener("show.bs.collapse", handleCollapseShow);
      collapseEl.removeEventListener("hidden.bs.collapse", handleCollapseHide);
    });
  };

  const handleCollapseShow = (event) => {
    event.target.closest("li").classList.add("active");
  };

  const handleCollapseHide = (event) => {
    event.target.closest("li").classList.remove("active");
  };

  const toggleSidebar = useCallback(() => {
    console.log(
      "toggleSidebar called, isMobile:",
      isMobile,
      "current isMobileOpen:",
      isMobileOpen,
    );
    if (isMobile) {
      // On mobile, toggle overlay open/close
      setIsMobileOpen((prev) => {
        const newState = !prev;
        console.log("Toggling mobile sidebar:", prev, "->", newState);
        return newState;
      });
    } else {
      // On desktop, toggle mini mode
      setIsMini((prevMini) => !prevMini);
    }
  }, [isMobile]);

  // Listen for toggle events from header
  useEffect(() => {
    const handleToggleSidebar = () => {
      // Check current window width to determine if mobile
      const currentIsMobile = window.innerWidth < 1200;
      console.log(
        "Event received, currentIsMobile:",
        currentIsMobile,
        "stored isMobile:",
        isMobile,
      );

      if (currentIsMobile) {
        setIsMobileOpen((prev) => {
          const newState = !prev;
          console.log("Setting isMobileOpen from", prev, "to", newState);
          return newState;
        });
      } else {
        setIsMini((prevMini) => !prevMini);
      }
    };

    window.addEventListener("toggleSidebar", handleToggleSidebar);
    return () => {
      window.removeEventListener("toggleSidebar", handleToggleSidebar);
    };
  }, [isMobile]);

  // Close sidebar on mobile when clicking outside
  useEffect(() => {
    if (!isMobile || !isMobileOpen) return;

    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        // Check if click is not on the toggle button
        const toggleButton = document.querySelector('[data-toggle="sidebar"]');
        if (toggleButton && !toggleButton.contains(event.target)) {
          setIsMobileOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobile, isMobileOpen]);

  const handleItemClick = (itemName) => {
    setActiveItem(itemName);
    localStorage.setItem("activeNavItem", itemName);
    // Close sidebar on mobile when item is clicked
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  const isActive = (itemName) => {
    return activeItem === itemName ? "active" : "";
  };

  // Determine home path based on user type
  const getHomePath = () => {
    // If user is Artist (even if also User), go to Artist dashboard
    if (userTypes === "Artist") {
      return "/artist/dashboard";
    }

    // Only User
    if (userTypes === "User") {
      return "/";
    }

    // Fallback
    return "/";
  };

  const sidebarClass = `sidebar sidebar-base sidebar-white sidebar-default shepherd-enabled shepherd-target ${
    isMini ? "sidebar-mini" : ""
  } ${isMobile && isMobileOpen ? "sidebar-open-mobile" : ""} ${
    isMobile && !isMobileOpen ? "sidebar-closed-mobile" : ""
  }`;
  // Debug: Log state changes
  useEffect(() => {
    console.log(
      "Sidebar state - isMobile:",
      isMobile,
      "isMobileOpen:",
      isMobileOpen,
    );
  }, [isMobile, isMobileOpen]);

  // console.log("isMobile", isMobile);
  return (
    <>
      {/* Mobile backdrop overlay */}
      {isMobile && isMobileOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setIsMobileOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1039,
          }}
        />
      )}
      <aside
        ref={sidebarRef}
        className={sidebarClass}
        id="first-tour"
        data-toggle="main-sidebar"
        data-sidebar="responsive"
        style={{
          ...(isMobile && {
            position: "fixed",
            top: 0,
            bottom: 0,
            left: 0,
            width: "280px",
            zIndex: 1040,
            transform: isMobileOpen ? "translateX(0)" : "translateX(-100%)",
            transition: "transform 0.3s ease-in-out",
          }),
        }}
      >
        <div className="sidebar-header d-flex align-items-center justify-content-center">
          <Link
            to={getHomePath()}
            className="navbar-brand"
            onClick={() => handleItemClick("home")}
          >
            <div className="logo-main">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/logo.png`}
                className="logo-normal img-fluid"
                alt="logo"
              />
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/logo.png`}
                className="logo-normal dark-normal img-fluid"
                alt="logo-dark"
              />
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/logo-min.png`}
                className="logo-mini img-fluid"
                alt="logo-mini"
              />
            </div>
          </Link>
          <div
            className="sidebar-toggle"
            data-toggle="sidebar"
            data-active="true"
            onClick={toggleSidebar}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && toggleSidebar()}
          >
            <i className="icon">
              {isMobile ? (
                // Mobile: Show hamburger menu icon when closed, X icon when open
                isMobileOpen ? (
                  <svg
                    className="icon-10"
                    width={20}
                    height={20}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                  >
                    <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg
                    className="icon-10"
                    width={20}
                    height={20}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                  >
                    <path d="M3 12h18M3 6h18M3 18h18" strokeLinecap="round" />
                  </svg>
                )
              ) : (
                // Desktop: Show double left arrows (existing icon)
                <svg
                  className="icon-10"
                  width={10}
                  height={10}
                  viewBox="0 0 8 8"
                  fill="none"
                >
                  <path
                    d="M7.29853 8C7.11974 8 6.94002 7.93083 6.80335 7.79248L3.53927 4.50446C3.40728 4.37085 3.33333 4.18987 3.33333 4.00036C3.33333 3.81179 3.40728 3.63081 3.53927 3.4972L6.80335 0.207279C7.07762 -0.069408 7.52132 -0.069408 7.79558 0.209174C8.06892 0.487756 8.06798 0.937847 7.79371 1.21453L5.02949 4.00036L7.79371 6.78618C8.06798 7.06286 8.06892 7.51201 7.79558 7.79059C7.65892 7.93083 7.47826 8 7.29853 8Z"
                    fill="white"
                  />
                  <path
                    d="M3.96552 8C3.78673 8 3.60701 7.93083 3.47034 7.79248L0.206261 4.50446C0.0742745 4.37085 0.000325203 4.18987 0.000325203 4.00036C0.000325203 3.81179 0.0742745 3.63081 0.206261 3.4972L3.47034 0.207279C3.74461 -0.069408 4.18831 -0.069408 4.46258 0.209174C4.73591 0.487756 4.73497 0.937847 4.4607 1.21453L1.69649 4.00036L4.4607 6.78618C4.73497 7.06286 4.73591 7.51201 4.46258 7.79059C4.32591 7.93083 4.14525 8 3.96552 8Z"
                    fill="white"
                  />
                </svg>
              )}
            </i>
          </div>
        </div>

        <div
          className="sidebar-body pt-0 data-scrollbar"
          style={{ overflowY: "auto", scrollbarWidth: "none" }}
        >
          <div className="scroll-content">
            <div className="sidebar-list">
              {/* User Menu — show ONLY if User exists and Artist does NOT exist */}
              {userTypes === "User" && (
                // && !userTypes.includes("Artist")
                <NavSection
                  title="Main"
                  items={[
                    { name: "home", label: "Home", icon: "home", path: "/" },
                    {
                      name: "trending",
                      label: "Trending",
                      icon: "whatshot",
                      path: "/trending",
                    },
                    {
                      name: "podcasts",
                      label: "Podcasts",
                      icon: "podcasts",
                      path: "/podcasts",
                    },
                    {
                      name: "store",
                      label: "Store",
                      icon: "shopping_cart",
                      path: "/",
                    },
                    {
                      name: "subscription",
                      label: "Subscription",
                      icon: "subscriptions",
                      path: "/subscription",
                    },
                    {
                      name: "events",
                      label: "Events",
                      icon: "event",
                      path: "/events",
                    },
                    {
                      name: "news",
                      label: "News",
                      icon: "article",
                      path: "/news",
                    },
                    {
                      name: "playlist",
                      label: "Playlist",
                      icon: "queue_music",
                      path: "/playlist",
                    },
                  ]}
                  isActive={isActive}
                  onItemClick={handleItemClick}
                />
              )}

              {/* Artist Menu — show if Artist exists (even if User also exists) */}
              {userTypes === "Artist" && (
                <NavSection
                  title="Artist"
                  items={[
                    {
                      name: "dashboard",
                      label: "Dashboard",
                      icon: "dashboard",
                      path: "/artist/dashboard",
                    },
                    {
                      name: "album",
                      label: "Album",
                      icon: "album",
                      path: "/artist/album",
                    },
                    {
                      name: "song",
                      label: "Song",
                      icon: "whatshot",
                      path: "/artist/song",
                    },
                    {
                      name: "podcasts",
                      label: "Podcasts",
                      icon: "podcasts",
                      path: "/artist/podcasts",
                    },
                    {
                      name: "events",
                      label: "Events",
                      icon: "event",
                      path: "/artist/events",
                    },
                  ]}
                  isActive={isActive}
                  onItemClick={handleItemClick}
                />
              )}
            </div>
          </div>
        </div>
        <div className="sidebar-footer"></div>
      </aside>
    </>
  );
}

// Separate component for nav sections
const NavSection = ({ title, items, isActive, onItemClick }) => (
  <ul className="navbar-nav iq-main-menu" id="sidebar-menu">
    <li className="nav-item static-item mt-2">
      <a
        className="nav-link static-item disabled text-start"
        href="#/"
        tabIndex={-1}
      >
        <span className="custom-icon default-icon text-uppercase">{title}</span>
        <span
          className="mini-icon"
          data-bs-toggle="tooltip"
          data-bs-placement="right"
        >
          -
        </span>
      </a>
    </li>
    {items.map((item) => (
      <li key={item.name} className="nav-item">
        <Link
          className={`nav-link ${isActive(item.name)}`}
          to={item.path}
          onClick={() => onItemClick(item.name)}
        >
          <span className="material-icons">{item.icon}</span>
          <span className="item-name">{item.label}</span>
        </Link>
      </li>
    ))}
  </ul>
);

export default Sidebar;
